/* Legal & compliance pages required by Meta App Review.
 * These are static, public, no-auth pages reachable at:
 *   /privacy
 *   /terms
 *   /data-deletion
 */
import { Link } from "react-router-dom";

const Container = ({ children, title }) => (
  <div className="min-h-screen bg-gray-50">
    <div className="max-w-3xl mx-auto px-6 py-12">
      <Link to="/" className="text-xs text-gray-500 hover:text-brand">← Back to Codira Ad Studio</Link>
      <h1 className="font-display text-4xl text-gray-900 mt-3 mb-2">{title}</h1>
      <p className="text-xs text-gray-400 mb-8">Last updated: February 2026</p>
      <div className="prose prose-sm max-w-none text-gray-700 space-y-4">{children}</div>
    </div>
  </div>
);

export function PrivacyPolicy() {
  return (
    <Container title="Privacy Policy">
      <h2 className="font-semibold text-lg text-gray-900">1. Information we collect</h2>
      <p>Codira Ad Studio ("Codira", "we", "our") collects the following information when you use our service at studio.codira.com:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Account info you provide (email, name)</li>
        <li>Ad campaigns, variations, and rendered video assets you create</li>
        <li>OAuth tokens for ad platforms you choose to connect (e.g. Meta), used only to perform actions you explicitly request</li>
        <li>Basic analytics about how you use the product</li>
      </ul>

      <h2 className="font-semibold text-lg text-gray-900 mt-6">2. How we use it</h2>
      <p>We use your data only to provide and improve the service: rendering your ads, publishing to platforms you connect, and showing you performance metrics. We do not sell your data. We do not share your data with third parties except as needed to perform the service you request (e.g. uploading an MP4 to Meta when you click Publish).</p>

      <h2 className="font-semibold text-lg text-gray-900 mt-6">3. Meta Platform data</h2>
      <p>When you connect your Meta (Facebook/Instagram) account, we receive an access token plus the ad accounts, Pages, and insights data that the Meta permissions you granted allow us to read. We store this only on our servers and use it only to perform actions you initiate inside Codira Ad Studio.</p>

      <h2 className="font-semibold text-lg text-gray-900 mt-6">4. Storage & security</h2>
      <p>Data is stored on MongoDB Atlas and AWS S3 (US-East). Access tokens are stored in a restricted database collection. All connections use TLS.</p>

      <h2 className="font-semibold text-lg text-gray-900 mt-6">5. Your rights</h2>
      <p>You can request deletion of your account and all associated data at any time by emailing <a className="text-brand" href="mailto:privacy@codira.com">privacy@codira.com</a> or by following the instructions on our <Link to="/data-deletion" className="text-brand">Data Deletion</Link> page.</p>

      <h2 className="font-semibold text-lg text-gray-900 mt-6">6. Contact</h2>
      <p>Questions? Email <a className="text-brand" href="mailto:privacy@codira.com">privacy@codira.com</a>.</p>
    </Container>
  );
}

export function Terms() {
  return (
    <Container title="Terms of Service">
      <h2 className="font-semibold text-lg text-gray-900">1. Service</h2>
      <p>Codira Ad Studio provides tools to design, render, and publish animated code-style video ads. By using the service you agree to these terms.</p>

      <h2 className="font-semibold text-lg text-gray-900 mt-6">2. Your content</h2>
      <p>You retain ownership of all marketing copy, code sequences, and rendered videos you create. You're responsible for ensuring you have the rights to any branding, claims, or third-party content you include.</p>

      <h2 className="font-semibold text-lg text-gray-900 mt-6">3. Connected platforms</h2>
      <p>When you connect Meta or other ad platforms, you authorize us to perform actions on your behalf only as explicitly requested through the Codira UI (e.g. listing ad accounts, uploading a video, creating a paused ad). We never auto-publish or auto-spend without your action.</p>

      <h2 className="font-semibold text-lg text-gray-900 mt-6">4. Acceptable use</h2>
      <p>You agree not to use Codira to publish content that violates the policies of the destination platforms (Meta Advertising Policies, etc.) or any applicable law.</p>

      <h2 className="font-semibold text-lg text-gray-900 mt-6">5. Liability</h2>
      <p>The service is provided "as is" without warranty. Codira is not liable for ad-platform decisions about your ads, billing charges from connected ad platforms, or any indirect damages arising from use of the service.</p>

      <h2 className="font-semibold text-lg text-gray-900 mt-6">6. Termination</h2>
      <p>You may stop using the service at any time. We may suspend accounts that violate these terms.</p>

      <h2 className="font-semibold text-lg text-gray-900 mt-6">7. Contact</h2>
      <p>Email <a className="text-brand" href="mailto:hello@codira.com">hello@codira.com</a>.</p>
    </Container>
  );
}

export function DataDeletion() {
  const code = new URLSearchParams(window.location.search).get("code");
  return (
    <Container title="Data Deletion">
      <p>To delete your Codira Ad Studio account and all associated data:</p>

      <ol className="list-decimal pl-6 space-y-2">
        <li>Email <a className="text-brand" href="mailto:privacy@codira.com?subject=Delete%20my%20account">privacy@codira.com</a> from the address associated with your account.</li>
        <li>Reply to our confirmation email within 7 days.</li>
        <li>We delete your account, workspace, campaigns, rendered assets, and any stored ad-platform tokens within 30 days.</li>
      </ol>

      <p className="mt-4">
        If you connected Meta and want to revoke just the Meta connection (keeping your Codira account), open Codira → Channels → Disconnect.
      </p>

      <p className="mt-4">
        You can also revoke the Codira app from your{" "}
        <a className="text-brand" href="https://www.facebook.com/settings?tab=business_tools" target="_blank" rel="noreferrer">
          Facebook Settings → Business Integrations
        </a> page at any time.
      </p>

      {code && (
        <div className="mt-6 bg-white border border-gray-200 rounded-lg p-4 text-sm">
          <p className="font-medium text-gray-900">Deletion confirmation code</p>
          <p className="text-xs text-gray-500 mt-1">Reference this code if you contact support about your request:</p>
          <p className="font-mono text-xs mt-2 bg-gray-50 p-2 rounded select-all">{code}</p>
        </div>
      )}
    </Container>
  );
}
